import { Check, X } from 'lucide-react';
import { PASSWORD_REQUIREMENTS, PasswordRequirement } from '../hooks/useFormValidation';
import { COLORS } from '../constants';

interface PasswordStrengthIndicatorProps {
  password: string;
  requirements?: boolean[];
  strength?: number;
}

/**
 * PasswordStrengthIndicator Component - Indicador visual de força da senha
 *
 * Exibe os requisitos de senha e a força da senha em tempo real
 *
 * @example
 * ```tsx
 * <PasswordStrengthIndicator
 *   password={password}
 *   requirements={checkPasswordRequirements(password)}
 *   strength={calculatePasswordStrength(password)}
 * />
 * ```
 */
export const PasswordStrengthIndicator = ({
  password: _password,
  requirements = [],
  strength = 0,
}: PasswordStrengthIndicatorProps) => {
  // Calculate strength level and color
  const getStrengthInfo = () => {
    if (strength === 0) {
      return {
        label: 'Muito fraca',
        color: COLORS.text.darkGray,
        bgColor: 'rgba(136, 136, 136, 0.2)',
      };
    }
    if (strength < 40) {
      return {
        label: 'Fraca',
        color: COLORS.error,
        bgColor: 'rgba(229, 57, 53, 0.2)',
      };
    }
    if (strength < 60) {
      return {
        label: 'Média',
        color: COLORS.warning,
        bgColor: 'rgba(255, 152, 0, 0.2)',
      };
    }
    if (strength < 80) {
      return {
        label: 'Boa',
        color: COLORS.primary,
        bgColor: 'rgba(244, 204, 4, 0.2)',
      };
    }
    return {
      label: 'Muito boa',
      color: COLORS.success,
      bgColor: 'rgba(76, 175, 80, 0.2)',
    };
  };

  const strengthInfo = getStrengthInfo();

  return (
    <div className="bg-[#2a2a2a] rounded-md p-3 border border-[#333333] w-[260px] shadow-lg">
      {/* Header */}
      <div className="mb-2">
        <div className="text-white font-semibold text-xs mb-1.5">
          Força da senha
        </div>

        {/* Strength Bar */}
        <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden mb-1.5">
          <div
            className="h-full transition-all duration-300 ease-out rounded-full"
            style={{
              width: `${strength}%`,
              backgroundColor: strengthInfo.color,
            }}
            role="progressbar"
            aria-valuenow={strength}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Força da senha: ${strengthInfo.label}`}
          />
        </div>

        {/* Strength Label */}
        <div
          className="text-[11px] font-medium px-1.5 py-0.5 rounded inline-block"
          style={{
            color: strengthInfo.color,
            backgroundColor: strengthInfo.bgColor,
          }}
        >
          {strengthInfo.label}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-[#333333] my-2" />

      {/* Requirements List */}
      <div>
        <div className="text-[#CCCCCC] text-[11px] mb-1.5">
          Sua senha deve conter:
        </div>
        <ul className="space-y-1" role="list">
          {PASSWORD_REQUIREMENTS.map((requirement: PasswordRequirement, index: number) => {
            const isMet = requirements[index] || false;
            return (
              <li
                key={requirement.label}
                className="flex items-center gap-1.5 text-[11px] transition-colors duration-200"
              >
                <span
                  className={`flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                    isMet
                      ? 'bg-[#4CAF50]'
                      : 'bg-[#1a1a1a] border border-[#333333]'
                  }`}
                  aria-label={isMet ? 'Requisito atendido' : 'Requisito não atendido'}
                >
                  {isMet ? (
                    <Check className="w-2 h-2 text-white" aria-hidden="true" />
                  ) : (
                    <X className="w-2 h-2 text-[#666666]" aria-hidden="true" />
                  )}
                </span>
                <span
                  className={`leading-tight ${
                    isMet ? 'text-[#4CAF50]' : 'text-[#888888]'
                  }`}
                >
                  {requirement.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
